import { Tooltip } from '@vizit/atoms';
import { WarningTriangleIcon } from '@vizit/icons';
import { colors, cx } from '@vizit/theme';
import * as React from 'react';
import { TooltipTriggerStateContext } from 'react-aria-components';
import { type BenchmarkOption, imagesNeededLabel } from './benchmark-option';

interface BenchmarkUnderMinimumIconProps {
  /** The disabled option; its minimum and image count name the icon for assistive tech. */
  option: BenchmarkOption;
  /** Icon size in pixels. */
  size?: number;
  /** Additional classes for the icon. */
  className?: string;
  /**
   * Whether hovering the icon shows "N more images needed". For rows with no hover card of their own; the row should
   * also carry the reason as its description, since a disabled row cannot take focus to reach a tooltip.
   */
  showTooltip?: boolean;
}

/**
 * The warning shown next to a custom benchmark listed under its minimum image count, wherever benchmarks are
 * picked. Reads out why the row cannot be chosen.
 */
function BenchmarkUnderMinimumIcon({
  option,
  size = 16,
  className,
  showTooltip = false,
}: BenchmarkUnderMinimumIconProps) {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const [isHovered, setIsHovered] = React.useState(false);
  // React Aria's Tooltip reads its open state from this context even when it is placed by `triggerRef`.
  const tooltipState = React.useMemo(
    () => ({ isOpen: isHovered, open: () => setIsHovered(true), close: () => setIsHovered(false) }),
    [isHovered],
  );

  const icon = (
    <WarningTriangleIcon
      size={size}
      color={colors.warning['500']}
      role="img"
      aria-label={`Needs at least ${option.minimumImages} images, has ${option.imageCount}`}
      className={cx('shrink-0', !showTooltip && className)}
    />
  );

  if (!showTooltip) {
    return icon;
  }

  // A disabled list row answers neither hover nor focus, so the pointer is tracked on the icon itself.
  return (
    <>
      <span
        ref={anchorRef}
        className={cx('flex shrink-0', className)}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
      >
        {icon}
      </span>
      <TooltipTriggerStateContext.Provider value={tooltipState}>
        <Tooltip triggerRef={anchorRef} placement="top">
          {imagesNeededLabel(option)}
        </Tooltip>
      </TooltipTriggerStateContext.Provider>
    </>
  );
}

export { BenchmarkUnderMinimumIcon };
export type { BenchmarkUnderMinimumIconProps };
